import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MdVerified } from 'react-icons/md'
import { FaStar, FaBoxOpen, FaShoppingBag, FaArrowRight } from 'react-icons/fa'

import useSellerPresence from '../../hooks/useSellerPresence'
import SellerPresence from './SellerPresence'
import { getUsableSellerFrame } from '../../utils/frames'
import { checkSellerBadges } from '../../utils/badges'

// Vahid Itemzo satıcı kartı — həm "Önə çıxan satıcılar" (ana səhifə), həm də
// /magazalar səhifəsi eyni dizaynı paylaşsın deyə SellerSpotlight-dakı kart
// buradan istifadə olunur. Data: /sellers/active (shopInfo, stats, frame, badges).
//
// Bütün sahələr opsionaldır — köhnə/şəkilsiz/çərçivəsiz satıcılarda da
// təhlükəsiz render olunur (frame/badge util-ləri revoked/expired-i süzür).

const COVER_GRADS = [
    'from-brand-600 via-brand-700 to-indigo-700',
    'from-fuchsia-600 via-purple-700 to-indigo-700',
    'from-sky-600 via-indigo-600 to-brand-700',
    'from-emerald-600 via-teal-700 to-brand-700',
]
const pickGrad = (id = '') => COVER_GRADS[(String(id).charCodeAt(id.length - 1) || 0) % COVER_GRADS.length]
const DOTS = { backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)', backgroundSize: '13px 13px' }

const SellerProfileCard = ({ seller }) => {
    const { t } = useTranslation()
    const shop = seller?.shopInfo || {}
    const target = `/shop/${shop.username || seller?._id}`
    const { isOnline, statusText } = useSellerPresence(seller?._id)
    const grad = pickGrad(seller?._id)
    const frame = getUsableSellerFrame(seller)
    const topBadges = checkSellerBadges(seller).slice(0, 3)
    const sales = Number(seller?.stats?.totalSales || 0)

    return (
        <Link
            to={target}
            className='group flex flex-col overflow-hidden rounded-card border border-slate-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-200 hover:shadow-card-hover'
        >
            {/* COVER — şəkil yoxdursa naxışlı qradiyent (düz blok deyil) */}
            <div className={`relative h-20 sm:h-16 overflow-hidden bg-gradient-to-br ${grad}`}>
                {shop.coverImage
                    ? <img src={shop.coverImage} alt='' loading='lazy' decoding='async' onError={(e) => { e.currentTarget.style.display = 'none' }} className='h-full w-full object-cover opacity-95 transition-transform duration-500 group-hover:scale-105' />
                    : <div className='absolute inset-0' style={DOTS} aria-hidden='true' />}
                <div className='pointer-events-none absolute -right-5 -top-7 h-20 w-20 rounded-full bg-white/15 blur-2xl' aria-hidden='true' />
                {/* shimmer sweep on hover */}
                <div className='pointer-events-none absolute inset-0 overflow-hidden opacity-0 transition-opacity duration-300 group-hover:opacity-100' aria-hidden='true'>
                    <div className='absolute inset-y-0 -left-1/3 w-1/3 skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-shimmer' />
                </div>
                {/* status badge */}
                <span className={`absolute right-2 top-2 inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[9px] font-bold backdrop-blur ${isOnline ? 'bg-emerald-400/90 text-emerald-950' : 'bg-white/85 text-slate-700'}`}>
                    {isOnline ? t('seller.activeShop') : t('seller.verified')}
                </span>
            </div>

            <div className='px-4 pb-4'>
                {/* avatar + animasiyalı çərçivə (mövcud frame sistemi) */}
                <div className='-mt-9 mb-2 frame-container frame-md transition-transform duration-300 group-hover:scale-105'>
                    <div className={`${frame.className} h-16 w-16 overflow-hidden bg-white p-0.5`}>
                        {shop.logo
                            ? <img src={shop.logo} alt={shop.shopName || 'Mağaza'} onError={(e) => { e.currentTarget.style.display = 'none' }} className='h-full w-full rounded-full object-cover' />
                            : <div className='flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-indigo-600 text-white font-black text-lg'>{(shop.shopName || 'M')[0].toUpperCase()}</div>}
                    </div>
                    {frame.isPremium && <span className='premium-badge !px-1.5 !py-0.5 !text-[9px]'>✨</span>}
                </div>

                <div className='flex items-center gap-1'>
                    <h3 className='truncate text-sm font-bold text-slate-800 group-hover:text-brand-700 transition-colors'>{shop.shopName || 'Adsız mağaza'}</h3>
                    <MdVerified className='shrink-0 text-brand-500' size={14} aria-label={t('seller.verified')} />
                </div>

                {/* qazanılmış nişanlar (tooltip ilə) */}
                {topBadges.length > 0 && (
                    <div className='mt-1.5 flex items-center gap-1'>
                        {topBadges.map(badge => (
                            <span key={badge.id} title={badge.name} className='text-base leading-none cursor-default transition-transform hover:scale-110'>{badge.emoji}</span>
                        ))}
                    </div>
                )}
                <div className='mt-0.5 flex items-center gap-2'>
                    {shop.username && <p className='truncate text-[11px] text-slate-400'>@{shop.username}</p>}
                    {seller?._id && <SellerPresence isOnline={isOnline} statusText={statusText} size='sm' />}
                </div>

                <div className='mt-3 flex flex-wrap items-center gap-2'>
                    <span className='inline-flex items-center gap-1 rounded-pill bg-amber-50 px-2 py-1 text-[11px] font-bold text-amber-700'>
                        <FaStar size={9} /> {Number(seller?.stats?.rating || 0).toFixed(1)}
                        {Number(seller?.stats?.totalReviews || 0) > 0 && <span className='font-semibold text-amber-600/80'>({seller.stats.totalReviews})</span>}
                    </span>
                    <span className='inline-flex items-center gap-1 rounded-pill bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600'><FaBoxOpen size={9} /> {seller?.productCount || 0} {t('seller.productUnit')}</span>
                    {sales > 0 && (
                        <span className='inline-flex items-center gap-1 rounded-pill bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700'><FaShoppingBag size={9} /> {sales} {t('seller.saleUnit')}</span>
                    )}
                </div>

                <span className='mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-card bg-brand-50 px-3 py-2 text-xs font-bold text-brand-700 transition-colors group-hover:bg-brand-600 group-hover:text-white'>
                    {t('seller.viewShop')} <FaArrowRight size={10} className='transition-transform group-hover:translate-x-0.5' />
                </span>
            </div>
        </Link>
    )
}

export default SellerProfileCard
